import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowUpRight, CaretDown, CaretLeft, CaretRight, CaretUpDown, FileCsv, FileText, MicrosoftExcelLogo, SpinnerGap, X } from '@phosphor-icons/react';
import { auswahlUnter } from './layout';
import type { WidgetDef, WidgetFarbe } from './typen';

/** Farbschema der Widgets (Prototyp TH): Grundfarbe als RGB, Kicker-Farbe. */
const TH: Record<WidgetFarbe, { rgb: string; kicker: string }> = {
  gold: { rgb: '226,192,141', kicker: '#e2c08d' },
  blau: { rgb: '10,132,255', kicker: '#64b5ff' },
  gruen: { rgb: '48,209,88', kicker: '#6fe08f' },
  violett: { rgb: '191,90,242', kicker: '#d69bff' },
  orange: { rgb: '255,159,10', kicker: '#ffc266' },
  rot: { rgb: '255,69,58', kicker: '#ff8a80' },
  grau: { rgb: '142,142,147', kicker: 'rgba(235,235,245,0.75)' },
  tuerkis: { rgb: '100,210,255', kicker: '#8be3ff' },
  indigo: { rgb: '94,92,230', kicker: '#a5a3ff' },
  pink: { rgb: '255,55,95', kicker: '#ff8aa5' },
};

const UNTER: CSSProperties = { fontSize: 11.5, fontWeight: 500, color: 'rgba(235,235,245,0.6)' };
const RUND: CSSProperties = { width: 28, height: 28, borderRadius: 14, background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff' };

function Kachel({ farbe, icon: IconKomp, laeuft }: { farbe: WidgetFarbe; icon: WidgetDef['icon']; laeuft?: boolean }) {
  const t = TH[farbe];
  const gold = farbe === 'gold';
  return (
    <span className="pille-widget-icon flex shrink-0 items-center justify-center"
      style={{
        width: 42, height: 42, borderRadius: 13,
        background: gold ? 'linear-gradient(180deg,#f0d3a3,#c9a063)' : `linear-gradient(180deg, rgba(255,255,255,0.3), rgba(255,255,255,0) 55%), rgb(${t.rgb})`,
        boxShadow: `0 8px 18px -8px rgba(${t.rgb},0.8)`, color: gold ? '#2a1d0e' : '#fff',
      }}>
      {laeuft ? <SpinnerGap size={21} weight="bold" className="pille-dreh" /> : <IconKomp size={21} weight={gold ? 'bold' : 'fill'} />}
    </span>
  );
}

/**
 * EINE generische Karte für alle Widget-Typen der Steuerung (Handoff Pille §6):
 * Aktion, Export, Auswahl, Schalter, Segment, Kennzahl, Schritt.
 */
export function Widget({ w, mobil }: { w: WidgetDef; mobil: boolean }) {
  const t = TH[w.farbe];
  const [hover, setHover] = useState(false);
  const [tipp, setTipp] = useState(false);
  const [exportOffen, setExportOffen] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectId = useId();
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const rein = () => {
    setHover(true);
    if (w.tooltip) timer.current = setTimeout(() => setTipp(true), 450);
  };
  const raus = () => { setHover(false); setTipp(false); if (timer.current) clearTimeout(timer.current); };

  const BgIcon = w.icon;
  const karte: CSSProperties = {
    minHeight: 132, borderRadius: 24, padding: 14, gap: 8,
    background: `linear-gradient(150deg, rgba(${t.rgb},${w.farbe === 'gold' ? 0.38 : 0.3}), rgba(${t.rgb},0.09) 65%, rgba(255,255,255,0.05))`,
    boxShadow: `inset 0 0.5px 0 rgba(255,255,255,0.2), inset 0 0 0 0.5px rgba(${t.rgb},0.4)`,
    transform: hover ? 'translateY(-4px) scale(1.025)' : 'none',
    filter: hover ? 'brightness(1.12) saturate(1.15)' : 'none',
  };

  const kopf = (rechts: ReactNode) => (
    <div className="flex items-start justify-between" style={{ gap: 8 }}>
      <Kachel farbe={w.farbe} icon={w.icon} laeuft={w.typ === 'aktion' && w.laeuft} />
      {rechts}
    </div>
  );
  const kicker = <span className="uppercase" style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', color: t.kicker, marginTop: 'auto' }}>{w.kicker}</span>;
  const titel = (text: string) => <span className="truncate" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>{text}</span>;

  let inhalt: ReactNode;
  let klickbar: (() => void) | null = null;

  switch (w.typ) {
    case 'aktion':
      klickbar = w.laeuft || w.gesperrt ? null : w.onClick;
      inhalt = (
        <>
          {kopf(<span aria-hidden className="flex items-center justify-center" style={RUND}><ArrowUpRight size={13} weight="bold" /></span>)}
          {kicker}{titel(w.titel)}{w.unter && <span className="truncate" style={UNTER}>{w.unter}</span>}
        </>
      );
      break;
    case 'export':
      klickbar = () => setExportOffen(o => !o);
      inhalt = (
        <>
          {kopf(<span aria-hidden className="flex items-center justify-center" style={RUND}>{exportOffen ? <X size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}</span>)}
          {kicker}{titel(w.titel)}
          {!exportOffen && <span className="truncate" style={UNTER}>{w.unter ?? 'Format wählen'}</span>}
          {exportOffen && (
            <div className="pille-pop flex flex-col" style={{ gap: 6 }} onClick={e => e.stopPropagation()}>
              {w.umfang && (
                <div role="radiogroup" aria-label="Umfang" className="flex" style={{ padding: 3, borderRadius: 12, background: 'rgba(0,0,0,0.28)', gap: 3 }}>
                  {w.umfang.optionen.map(o => {
                    const an = o.wert === w.umfang!.wert;
                    return (
                      <button key={o.wert} type="button" role="radio" aria-checked={an} onClick={() => w.umfang!.onChange(o.wert)}
                        className="flex-1 cursor-pointer truncate"
                        style={{ height: mobil ? 36 : 26, borderRadius: 9, border: 'none', fontSize: 11.5, fontWeight: an ? 700 : 600, background: an ? '#fff' : 'transparent', color: an ? '#111' : 'rgba(255,255,255,0.8)', fontFamily: 'inherit' }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
                {w.formate.map(f => {
                  const FIcon = /csv/i.test(f.id) ? FileCsv : /xls|excel/i.test(f.id) ? MicrosoftExcelLogo : FileText;
                  return (
                    <button key={f.id} type="button" title={f.unter} onClick={() => { void f.onClick(); setExportOffen(false); }}
                      className="flex cursor-pointer items-center justify-center truncate transition-transform active:scale-95"
                      style={{ height: mobil ? 44 : 38, gap: 6, borderRadius: 12, border: 'none', background: 'rgba(255,255,255,0.14)', color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' }}>
                      <FIcon size={17} weight="fill" color="#6fe08f" className="shrink-0" />
                      <span className="truncate">{f.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      );
      break;
    case 'auswahl': {
      const aktuell = w.optionen.find(o => o.wert === w.wert)?.label ?? w.wert;
      const gruppen = [...new Set(w.optionen.map(o => o.gruppe ?? ''))];
      inhalt = (
        <>
          {kopf(<span aria-hidden className="flex items-center justify-center" style={RUND}><CaretUpDown size={13} weight="bold" /></span>)}
          {kicker}{titel(aktuell)}
          <span className="truncate" style={UNTER}>{auswahlUnter(w.wert, w.standard, w.unter)}</span>
          {/* natives Select über der ganzen Karte (Tastatur, Mobil-Picker) */}
          <label htmlFor={selectId} className="sr-only">{w.kicker}</label>
          <select id={selectId} value={w.wert} onChange={e => w.onChange(e.target.value)}
            className="absolute inset-0 cursor-pointer opacity-0" style={{ colorScheme: 'dark' }}>
            {gruppen.map(g => (g
              ? <optgroup key={g} label={g}>{w.optionen.filter(o => o.gruppe === g).map(o => <option key={o.wert} value={o.wert}>{o.label}</option>)}</optgroup>
              : w.optionen.filter(o => !o.gruppe).map(o => <option key={o.wert} value={o.wert}>{o.label}</option>)))}
          </select>
          {w.zeitraum?.sichtbar && (
            <div className="relative z-10 grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 6 }}>
              {([['von', w.zeitraum.von, w.zeitraum.onVon], ['bis', w.zeitraum.bis, w.zeitraum.onBis]] as const).map(([l, v, on]) => (
                <input key={l} type="date" aria-label={l} value={v} onChange={e => on(e.target.value)}
                  style={{ height: mobil ? 44 : 34, borderRadius: 10, border: 'none', padding: '0 8px', background: 'rgba(0,0,0,0.28)', color: '#fff', fontSize: 12, fontWeight: 600, colorScheme: 'dark', fontFamily: 'inherit', minWidth: 0 }} />
              ))}
            </div>
          )}
        </>
      );
      break;
    }
    case 'schalter':
      klickbar = () => w.onChange(!w.an);
      inhalt = (
        <>
          {kopf(
            <span aria-hidden className="flex shrink-0" style={{ width: 51, height: 31, borderRadius: 16, padding: 2, background: w.an ? '#30d158' : 'rgba(120,120,128,0.36)', transition: 'background .25s' }}>
              <span style={{ width: 27, height: 27, borderRadius: 14, background: '#fff', boxShadow: '0 3px 8px rgba(0,0,0,0.3)', transform: w.an ? 'translateX(20px)' : 'none', transition: 'transform .25s cubic-bezier(.34,1.3,.64,1)' }} />
            </span>,
          )}
          {kicker}{titel(w.titel)}<span className="truncate" style={UNTER}>{w.unter ?? (w.an ? 'An' : 'Aus')}</span>
        </>
      );
      break;
    case 'segment':
      inhalt = (
        <>
          {kopf(null)}
          {kicker}{titel(w.titel)}
          <div role="radiogroup" aria-label={w.titel} className="flex" style={{ padding: 3, borderRadius: 12, background: 'rgba(0,0,0,0.28)', gap: 3 }}>
            {w.optionen.map(o => {
              const an = o.wert === w.wert;
              return (
                <button key={o.wert} type="button" role="radio" aria-checked={an} onClick={() => w.onChange(o.wert)}
                  className="min-w-0 flex-1 cursor-pointer truncate"
                  style={{ height: mobil ? 36 : 26, padding: '0 4px', borderRadius: 9, border: 'none', fontSize: 11.5, fontWeight: an ? 700 : 600, background: an ? '#fff' : 'transparent', color: an ? '#111' : 'rgba(255,255,255,0.8)', fontFamily: 'inherit', transition: 'background .2s' }}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </>
      );
      break;
    case 'kennzahl':
      inhalt = (
        <>
          {kopf(null)}
          {kicker}
          <div className="flex flex-wrap" style={{ gap: '8px 22px' }}>
            {w.werte.map(v => {
              const zahl = <span className="tabular-nums" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.05 }}>{v.zahl}</span>;
              const label = <span style={{ fontSize: 11.5, color: v.aktiv ? t.kicker : 'rgba(235,235,245,0.6)', fontWeight: v.aktiv ? 700 : 500 }}>{v.label}</span>;
              return v.onClick ? (
                <button key={v.label} type="button" onClick={v.onClick} aria-pressed={v.aktiv}
                  className="flex cursor-pointer flex-col items-start text-left"
                  style={{ border: 'none', background: v.aktiv ? `rgba(${t.rgb},0.22)` : 'transparent', borderRadius: 12, padding: '4px 8px', margin: '-4px -8px', color: '#fff', fontFamily: 'inherit', minHeight: mobil ? 44 : undefined }}>
                  {zahl}{label}
                </button>
              ) : (
                <span key={v.label} className="flex flex-col">{zahl}{label}</span>
              );
            })}
          </div>
        </>
      );
      break;
    case 'schritt': {
      const knopf = mobil ? 44 : 32;
      inhalt = (
        <>
          {kopf(null)}
          {kicker}
          <div className="flex items-center" style={{ gap: 8 }}>
            <button type="button" onClick={w.onZurueck} aria-label={w.zurueckLabel ?? 'Zurück'} className="flex shrink-0 cursor-pointer items-center justify-center" style={{ ...RUND, width: knopf, height: knopf, borderRadius: knopf / 2 }}>
              <CaretLeft size={14} weight="bold" />
            </button>
            <span className="min-w-0 flex-1 truncate text-center" style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>{w.titel}</span>
            <button type="button" onClick={w.onVor} aria-label={w.vorLabel ?? 'Weiter'} className="flex shrink-0 cursor-pointer items-center justify-center" style={{ ...RUND, width: knopf, height: knopf, borderRadius: knopf / 2 }}>
              <CaretRight size={14} weight="bold" />
            </button>
          </div>
        </>
      );
      break;
    }
  }

  const tastatur = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!klickbar || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); klickbar(); }
  };
  const ariaLabel = w.typ === 'aktion' || w.typ === 'export' || w.typ === 'schalter' ? `${w.kicker}: ${w.titel}` : undefined;

  return (
    <div className="relative min-w-0" onMouseEnter={rein} onMouseLeave={raus}>
      <div
        role={klickbar ? (w.typ === 'schalter' ? 'switch' : 'button') : undefined}
        aria-checked={w.typ === 'schalter' ? w.an : undefined}
        aria-expanded={w.typ === 'export' ? exportOffen : undefined}
        aria-label={ariaLabel}
        aria-disabled={w.typ === 'aktion' && (w.laeuft || w.gesperrt) ? true : undefined}
        tabIndex={klickbar ? 0 : undefined}
        onClick={klickbar ?? undefined}
        onKeyDown={tastatur}
        className={`pille-widget relative flex h-full flex-col overflow-hidden ${klickbar ? 'cursor-pointer active:scale-[.97]' : ''}`}
        style={karte}
      >
        <BgIcon aria-hidden size={92} weight="regular" className="pille-widget-bg pointer-events-none absolute" style={{ right: -12, bottom: -22, color: `rgba(${t.rgb},0.13)` }} />
        {inhalt}
      </div>
      {tipp && w.tooltip && !exportOffen && (
        <div role="tooltip" className="pille-tipp pointer-events-none absolute left-1/2 z-20 flex items-start"
          style={{ bottom: 'calc(100% + 8px)', transform: 'translateX(-50%)', maxWidth: 240, width: 'max-content', padding: '8px 12px', borderRadius: 12, gap: 7, background: 'rgba(44,44,46,0.96)', backdropFilter: 'blur(20px)', fontSize: 12, fontWeight: 600, lineHeight: 1.35, color: '#fff', boxShadow: '0 10px 30px -10px rgba(0,0,0,0.6)' }}>
          <span className="shrink-0 rounded-full" style={{ width: 6, height: 6, marginTop: 5, background: `rgb(${t.rgb})` }} />
          {w.tooltip}
        </div>
      )}
    </div>
  );
}
